import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  applyClientSideTableState,
  DataTable,
  useTableState,
  type ColumnDef,
} from '@/components/data-table'
import { AddButton } from '@/components/common/AddButton'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { StatusBadge } from '@/components/common/StatusBadge'
import { useStageInfoByConfigurations, type StageInfo } from '@/features/admin/stages/hooks'
import { useHaloUsers } from '@/features/halocrm-user/hooks'
import { useContracts } from './hooks'
import { useContractCreateSheet } from './components/ContractCreateSheet'
import type { ContractListItem } from './types'

const TABLE_ID = 'contracts'

/** "12 500,00 zł" gdy oba podane; "—" gdy total = null; sama liczba gdy currency puste. */
function formatContractValue(total: number | string | null | undefined, currency: string | null): string {
  if (total == null || total === '') return '—'
  const num = typeof total === 'number' ? total : Number(total)
  if (Number.isNaN(num)) return '—'
  try {
    return num.toLocaleString('pl-PL', {
      style: currency ? 'currency' : 'decimal',
      currency: currency ?? undefined,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  } catch {
    // Nieprawidłowy kod waluty - fallback bez stylowania.
    return `${num.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency ?? ''}`.trim()
  }
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pl-PL')
  } catch {
    return iso
  }
}

function assigneeLabel(c: ContractListItem, assigneeNames: Map<number, string>): string {
  if (c.assigneeUserId == null) return ''
  return assigneeNames.get(c.assigneeUserId) ?? `#${c.assigneeUserId}`
}

function buildColumns(
  stageInfo: Map<string, Map<string, StageInfo>>,
  assigneeNames: Map<number, string>,
): ColumnDef<ContractListItem>[] {
  const infoOf = (c: ContractListItem): StageInfo =>
    stageInfo.get(c.configurationCode)?.get(c.currentStageCode) ?? { label: c.currentStageCode, color: 'neutral' }
  return [
    {
      key: 'code',
      header: 'Numer',
      sortable: true,
      filter: { kind: 'text', placeholder: 'Filtruj numer...' },
      accessor: (c) => <span className="font-mono text-xs">{c.code}</span>,
      className: 'whitespace-nowrap',
      alwaysVisible: true,
    },
    {
      key: 'ordererName',
      header: 'Zamawiający',
      sortable: true,
      filter: { kind: 'text', placeholder: 'Filtruj zamawiającego...' },
      accessor: (c) => c.ordererName ?? <span className="text-slate-400">- brak -</span>,
      className: 'whitespace-nowrap',
    },
    {
      key: 'currentStageCode',
      header: 'Etap',
      sortable: true,
      filter: { kind: 'text', placeholder: 'Filtruj etap...' },
      accessor: (c) => {
        const { label, color } = infoOf(c)
        return <StatusBadge label={label} variant={color} title={c.currentStageCode} />
      },
      className: 'whitespace-nowrap',
    },
    {
      key: 'assigneeUserId',
      header: 'Opiekun',
      sortable: true,
      filter: { kind: 'text', placeholder: 'Filtruj opiekuna...' },
      accessor: (c) =>
        c.assigneeUserId == null
          ? <span className="text-amber-600">Nieprzypisana</span>
          : assigneeLabel(c, assigneeNames),
      className: 'whitespace-nowrap',
    },
    {
      key: 'totalAmount',
      header: 'Wartość',
      sortable: true,
      accessor: (c) => formatContractValue(c.totalAmount, c.currency),
      className: 'text-right tabular-nums whitespace-nowrap',
    },
    {
      key: 'updatedAt',
      header: 'Aktualizacja',
      sortable: true,
      filter: { kind: 'dateRange' },
      accessor: (c) => <span className="text-xs text-muted-foreground">{formatDateTime(c.updatedAt)}</span>,
      className: 'whitespace-nowrap',
    },
  ]
}

export function ContractListPage() {
  const navigate = useNavigate()
  const contractsQ = useContracts()
  const create = useContractCreateSheet()
  const { state, setSort, setSearch, setFilter, removeFilter, clearFilters } = useTableState(TABLE_ID)

  const configurationCodes = useMemo(
    () => (contractsQ.data?.items ?? []).map(c => c.configurationCode),
    [contractsQ.data],
  )
  const stageInfo = useStageInfoByConfigurations(configurationCodes)
  const haloUsers = useHaloUsers({ active: false, limit: 200 })
  const assigneeNames = useMemo(() => {
    const map = new Map<number, string>()
    haloUsers.data?.items.forEach((u) => map.set(u.id, u.fullName))
    return map
  }, [haloUsers.data])
  const columns = useMemo(() => buildColumns(stageInfo, assigneeNames), [stageInfo, assigneeNames])

  const filteredRows = useMemo(
    () =>
      applyClientSideTableState(contractsQ.data?.items ?? [], columns, state, {
        valueGetters: {
          code: (c) => c.code,
          ordererName: (c) => c.ordererName ?? '',
          currentStageCode: (c) => stageInfo.get(c.configurationCode)?.get(c.currentStageCode)?.label ?? c.currentStageCode,
          assigneeUserId: (c) => assigneeLabel(c, assigneeNames),
          totalAmount: (c) => c.totalAmount == null ? null : Number(c.totalAmount),
          updatedAt: (c) => c.updatedAt.slice(0, 10),
        },
        searchString: (c) => {
          const label = stageInfo.get(c.configurationCode)?.get(c.currentStageCode)?.label ?? ''
          return `${c.code} ${c.currentStageCode} ${label} ${c.ordererName ?? ''} ${assigneeLabel(c, assigneeNames)}`.toLowerCase()
        },
      }),
    [contractsQ.data, columns, state, stageInfo, assigneeNames],
  )

  return (
    <div className="flex flex-col w-full">
      <ListPageHeader
        title="Umowy"
        actions={<AddButton onClick={create.openCreate}>Dodaj umowę</AddButton>}
      />
      <div className="p-4">
        <DataTable<ContractListItem>
          tableId={TABLE_ID}
          data={filteredRows}
          columns={columns}
          rowKey={(c) => c.id}
          isLoading={contractsQ.isLoading}
          isError={!!contractsQ.error}
          errorState={contractsQ.error ? `Błąd: ${String(contractsQ.error)}` : undefined}
          onRowClick={(c) => navigate(`/contracts/${c.id}`)}
          sort={state.sort}
          onSortChange={setSort}
          state={state}
          searchable
          searchPlaceholder="Szukaj numeru / etapu / waluty..."
          onSearchChange={setSearch}
          onFilterChange={setFilter}
          onFilterRemove={removeFilter}
          onClearAll={clearFilters}
          emptyState="Brak umów do wyświetlenia."
        />
      </div>
      {create.sheet}
    </div>
  )
}
